import { useTheme } from '@mui/material/styles'
import { StytchB2B } from '@stytch/react/b2b'
import type {
	StytchB2BUIConfig} from '@stytch/vanilla-js/b2b';
import {
	AuthFlowType,
	B2BOAuthProviders,
	B2BProducts,
	StytchEventType,
} from '@stytch/vanilla-js/b2b'
import { useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import type { FC } from 'react'

/**
 * LoginOrSignup
 * Renders Stytch's prebuilt B2B auth UI using the Discovery flow.
 * - Uses Email Magic Links with redirect back to /authenticate
 */
type StytchStyles = {
	fontFamily?: string
	container?: {
		width?: string
		backgroundColor?: string
		borderColor?: string
		borderRadius?: string
		boxShadow?: string
		padding?: string
	}
	colors?: {
		primary?: string
		secondary?: string
		success?: string
		error?: string
	}
	buttons?: {
		primary?: {
			backgroundColor?: string
			textColor?: string
			borderColor?: string
			borderRadius?: string
		}
		secondary?: {
			backgroundColor?: string
			textColor?: string
			borderColor?: string
			borderRadius?: string
		}
	}
	inputs?: {
		borderColor?: string
		borderRadius?: string
		textColor?: string
		placeholderColor?: string
		backgroundColor?: string
	}
	logo?: {
		logoImageUrl?: string
	}
}
export const LoginOrSignup: FC = () => {
	const origin = typeof window !== 'undefined' ? window.location.origin : ''
	const navigate = useNavigate()
	const redirectUrl = `${origin}/authenticate`
	const theme = useTheme()
	const selectedOrganizationSlug = useRef<string | null>(null)

	const config: StytchB2BUIConfig = {
		// Offer Email Magic Links to start
		products: [B2BProducts.emailMagicLinks, B2BProducts.passwords, B2BProducts.oauth],
		oauthOptions: {
			providers: [
				{
					type: B2BOAuthProviders.Google,
				},
			],
			loginRedirectURL: redirectUrl,
			signupRedirectURL: redirectUrl,
			discoveryRedirectURL: redirectUrl,
		},
		// 60-minute session, Discovery flow for org creation/join
		sessionOptions: { sessionDurationMinutes: 360 },
		authFlowType: AuthFlowType.Discovery,
		// Explicitly set redirect URLs for discovery magic links
		emailMagicLinksOptions: {
			discoveryRedirectURL: redirectUrl,
		},
		disableCreateOrganization: true,
		directCreateOrganizationForNoMembership: true,
		directLoginForSingleMembership: {
			ignoreJitProvisioning: true,
			ignoreInvites: true,
			status: true,
		},
	}

	// Derive Stytch UI styles from the active MUI theme so branding stays in sync
	const styles: StytchStyles = useMemo(() => {
		return {
			fontFamily:
				"Telegraf, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'",
			container: {
				width: '100%',
				borderColor: theme.palette.divider,
				borderRadius: '4px',
			},
			colors: {
				primary: theme.palette.primary.main,
				secondary: theme.palette.secondary.main,
				success: theme.palette.success.main,
				error: theme.palette.error.main,
			},
			buttons: {
				primary: {
					backgroundColor: theme.palette.primary.main,
					textColor: theme.palette.primary.contrastText,
					borderColor: theme.palette.primary.main,
				},
				secondary: {
					backgroundColor: theme.palette.background.default,
					textColor: theme.palette.text.primary,
					borderColor: theme.palette.divider,
				},
			},
			inputs: {
				borderColor: theme.palette.divider,
				textColor: theme.palette.text.primary,
			},
			logo: {
				logoImageUrl: '/favicon.ico',
			},
		}
	}, [theme])

	return (
		<StytchB2B
			config={config}
			styles={styles}
			strings={{
				'organizationDiscovery.title': 'Select a Workspace',
			}}
			callbacks={{
				onEvent: (evt) => {
					if (
						evt.type === StytchEventType.B2BDiscoveryIntermediateSessionExchange ||
						evt.type === StytchEventType.B2BDiscoveryOrganizationsCreate
					) {
						selectedOrganizationSlug.current = evt.data.organization.organization_slug
					}
					if (evt.type === StytchEventType.AuthenticateFlowComplete) {
						navigate(`/${selectedOrganizationSlug.current}/grids`, { replace: true })
					}
				},
			}}
		/>
	)
}
